// Every answer of the form an entry came from (the fields without a system column), and the files it was sent with.
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, ButtonBase, CircularProgress, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, uploadFile } from '../api/client';
import { Button } from './button';
import { ConfirmDialog } from './common';
import { useToast } from './Toast';
import { CURRENCIES, UNITS } from '@re/form-ui';
import type { EntryFile, EntryFormField } from '../api/types';
import { getUiLanguage, tr } from '../lib/i18n';
import { InfoRow, SectionCard } from './org';

type L = { ar: string; en?: string } | null | undefined;
const text = (l: L) => (getUiLanguage() === 'en' && l?.en?.trim() ? l.en : l?.ar ?? '');
const num = (n: unknown) => (typeof n === 'number' ? n.toLocaleString('en-US') : String(n));
const SKIP = new Set(['section', 'info', 'image_upload', 'file_upload', 'request_type', 'property_category', 'property_type', 'property_features', 'location']);

function unitOf(f: EntryFormField, currency: string | null | undefined): string {
  const u = f.config?.unit as string | undefined;
  if (!u) return '';
  const def = u === 'currency' ? (currency ? CURRENCIES[currency] ?? { ar: currency, en: currency } : undefined) : UNITS[u];
  return def ? ` ${tr(def.ar, def.en)}` : ` ${u}`;
}

function show(f: EntryFormField, v: unknown, currency?: string | null): string | null {
  if (v === undefined || v === null || v === '') return null;
  const opt = (x: unknown) => text(f.config?.options?.find((o) => o.value === x)?.label) || String(x);
  switch (f.type) {
    case 'boolean':
      return v ? tr('نعم', 'Yes') : tr('لا', 'No');
    case 'radio':
    case 'dropdown':
      return opt(v);
    case 'checkbox_group':
      return Array.isArray(v) && v.length ? v.map(opt).join('، ') : null;
    case 'number':
    case 'currency':
      return `${num(v)}${unitOf(f, currency)}`;
    case 'number_range': {
      const r = v as { min?: number; max?: number };
      if (r.min == null && r.max == null) return null;
      const u = unitOf(f, currency);
      if (r.min != null && r.max != null) return `${num(r.min)} – ${num(r.max)}${u}`;
      return r.min != null ? `${tr('من', 'From')} ${num(r.min)}${u}` : `${tr('حتى', 'Up to')} ${num(r.max)}${u}`;
    }
    case 'date_range': {
      const r = v as { from?: string; to?: string };
      return [r.from, r.to].filter(Boolean).join(' – ') || null;
    }
    default:
      return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
}

/** The form's own questions (custom fields) with their answers, in form order. */
export function FormAnswersCard({ fields, answers, currency }: { fields: EntryFormField[]; answers?: Record<string, unknown> | null; currency?: string | null }) {
  if (!answers) return null;
  const rows = fields
    .filter((f) => !f.maps_to && !SKIP.has(f.type))
    .map((f) => ({ f, value: show(f, answers[f.key], currency) }))
    .filter((r) => r.value !== null);
  if (!rows.length) return null;
  return (
    <SectionCard title={tr('إجابات النموذج', 'Form answers')}>
      {rows.map(({ f, value }) =>
        f.type === 'textarea' ? (
          <Box key={f.key} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Typography variant="body2" color="text.secondary">{text(f.label)}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{value}</Typography>
          </Box>
        ) : (
          <InfoRow key={f.key} label={text(f.label)}>{value}</InfoRow>
        ),
      )}
    </SectionCard>
  );
}

const size = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const STAFF_KEY = 'staff_attachments';
const DOC_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';

/**
 * Files sent with the entry, grouped under the form field that asked for them. Opens each file in a new tab.
 * With `edit`, staff can add documents (under one of the form's upload questions, or «مرفقات أخرى») and remove them.
 */
export function AttachmentsCard({ files, fields, edit }: { files: EntryFile[]; fields: EntryFormField[]; edit?: { path: string; onChanged: () => void } }) {
  const { toast, toastError } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [target, setTarget] = useState(STAFF_KEY);
  const [remove, setRemove] = useState<EntryFile | null>(null);
  const uploadFields = fields.filter((f) => f.type === 'file_upload');
  const add = useMutation({
    mutationFn: async (list: File[]) => {
      const fileIds: string[] = [];
      for (const f of list) fileIds.push(await uploadFile(f));
      return api(`${edit!.path}/files`, { method: 'POST', body: { fileIds, fieldKey: target } });
    },
    onSuccess: () => { toast(tr('تمت إضافة المرفقات', 'Attachments added')); edit!.onChanged(); },
    onError: toastError,
  });
  const del = useMutation({
    mutationFn: (f: EntryFile) => api(`${edit!.path}/files/${f.id}`, { method: 'DELETE' }),
    onSuccess: () => { toast(tr('تم حذف المرفق', 'Attachment removed')); setRemove(null); edit!.onChanged(); },
    onError: toastError,
  });
  if (!files.length && !edit) return null;
  const groups = new Map<string, EntryFile[]>();
  for (const f of files) groups.set(f.field_key, [...(groups.get(f.field_key) ?? []), f]);
  const titleOf = (key: string) => (key === STAFF_KEY ? tr('مرفقات أخرى (أضافها الموظفون)', 'Other attachments (added by staff)') : text(fields.find((f) => f.key === key)?.label) || key);
  const pick = (key: string) => {
    setTarget(key);
    setMenu(null);
    inputRef.current?.click();
  };
  return (
    <SectionCard
      title={`${tr('المرفقات', 'Attachments')} (${files.length})`}
      action={edit && (
        <>
          <Button size="small" startIcon={add.isPending ? <CircularProgress size={14} /> : <AddIcon />} disabled={add.isPending} onClick={(e) => (uploadFields.length ? setMenu(e.currentTarget) : pick(STAFF_KEY))}>
            {tr('إضافة مرفق', 'Add attachment')}
          </Button>
          <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
            {uploadFields.map((f) => <MenuItem key={f.key} onClick={() => pick(f.key)}>{text(f.label)}</MenuItem>)}
            <MenuItem onClick={() => pick(STAFF_KEY)}>{tr('مرفقات أخرى', 'Other attachments')}</MenuItem>
          </Menu>
          <input
            ref={inputRef}
            hidden
            type="file"
            multiple
            accept={DOC_ACCEPT}
            onChange={(e) => {
              const list = Array.from(e.target.files ?? []);
              e.target.value = '';
              const bad = list.filter((f) => !DOC_ACCEPT.split(',').includes(f.type));
              if (bad.length) toast(tr('يُقبل PDF أو صورة (JPG / PNG / WEBP) فقط', 'Only PDF or images (JPG / PNG / WEBP)'), 'warning');
              const ok = list.filter((f) => !bad.includes(f));
              if (ok.length) add.mutate(ok);
            }}
          />
        </>
      )}
    >
      {!files.length ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 1.5, textAlign: 'center' }}>{tr('لا توجد مرفقات', 'No attachments')}</Typography>
      ) : (
        <Stack spacing={2}>
          {[...groups.entries()].map(([key, list]) => (
            <Box key={key}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{titleOf(key)}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {list.map((f) => {
                  const image = f.mime_type.startsWith('image/');
                  return (
                    <Box key={f.id} sx={{ position: 'relative' }}>
                      <ButtonBase
                        component="a"
                        href={f.url ?? undefined}
                        target="_blank"
                        rel="noopener"
                        disabled={!f.url}
                        sx={{ width: 120, flexDirection: 'column', alignItems: 'stretch', border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', textAlign: 'start' }}
                      >
                        <Box sx={{ height: 84, display: 'grid', placeItems: 'center', bgcolor: 'action.hover' }}>
                          {image && f.url ? (
                            <Box component="img" src={f.url} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : f.mime_type === 'application/pdf' ? (
                            <PictureAsPdfOutlinedIcon color="error" sx={{ fontSize: 36 }} />
                          ) : (
                            <AttachFileIcon sx={{ fontSize: 32 }} />
                          )}
                        </Box>
                        <Box sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" noWrap component="div" title={f.original_name ?? undefined}>{f.original_name || (image ? tr('صورة', 'Image') : 'PDF')}</Typography>
                          <Typography variant="caption" color="text.secondary">{size(f.size_bytes)}</Typography>
                        </Box>
                      </ButtonBase>
                      {edit && (
                        <IconButton
                          size="small"
                          aria-label={tr('حذف المرفق', 'Remove attachment')}
                          onClick={() => setRemove(f)}
                          sx={{ position: 'absolute', top: 4, insetInlineEnd: 4, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.7)' }, p: 0.4 }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
      <ConfirmDialog
        open={!!remove}
        danger
        title={tr('حذف المرفق', 'Remove attachment')}
        text={tr(`سيتم حذف «${remove?.original_name ?? ''}» من هذا الإدخال.`, `"${remove?.original_name ?? ''}" will be removed from this entry.`)}
        confirmLabel={tr('حذف', 'Remove')}
        busy={del.isPending}
        onConfirm={() => remove && del.mutate(remove)}
        onClose={() => setRemove(null)}
      />
    </SectionCard>
  );
}
